// «Производство / Захватки» — главный экран корпуса для начальника участка и руководителя (макет заказчика 22.10.2026).
// Сверху — корпуса проекта вкладками и выбор этажа; дальше показатели, захватки этажа, план/факт, график этажей,
// материалы, ключевые события и то, что требует решения. Техника и снабжение — отдельным этапом.

import { useState } from "react";
import { fmtDate, fmtNum, fmtTime, plural } from "../../shared/format";
import { useLive } from "../../shared/live";
import { navigate } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Bar, Button, Empty, Icon, Loading, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { FloorsGantt, PlanFactBars, Ring } from "../charts";
import { api, can, floorName, live, useCtx } from "../ctx";

const days = (n: number) => `${fmtNum(n, n % 1 ? 1 : 0)} ${plural(Math.ceil(n), "день", "дня", "дней")}`;
const GRIP_ST: Record<string, { label: string; cls: string }> = {
  done: { label: "Готова", cls: "s-done" },
  in_progress: { label: "В работе", cls: "s-planned" },
  behind: { label: "Отставание", cls: "s-loading" },
  problem: { label: "Проблема", cls: "s-conflict" },
  not_started: { label: "Не начата", cls: "" },
};

export function ProductionPage({ id }: { id: string }) {
  const { me } = useCtx();
  const toast = useToast();
  const [floor, setFloor] = useState<number | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState<any | null>(null);
  const d = useAsync<any>(() => api.get(`/buildings/${id}/production`, floor ? { floor } : undefined), [id, floor]);
  useLive(live, ["report.", "cycle.", "issue.", "alert."], () => d.reload(), 1000);
  const v = d.data;
  const manager = can(me, "overview");
  return (
    <>
      <TopBar title="Производство / Захватки">
        {v && v.floors.length > 0 && (
          <Select aria-label="Этаж" value={String(v.floor ?? "")} style={{ width: 170 }}
            onChange={(e) => setFloor(Number(e.target.value))}
            options={v.floors.map((f: any) => ({ value: String(f.number), label: `${floorName(f.number)}${f.number === v.floor_current ? " (сейчас)" : ""}` }))} />
        )}
        {manager && <Button size="small" disabled={aiBusy} onClick={async () => { setAiBusy(true); try { setAi(await api.get(`/buildings/${id}/ai/summary`)); } catch (e) { toast.error(e); } finally { setAiBusy(false); } }}>Сводка за день</Button>}
      </TopBar>
      <div className="content prod">
        {!v ? <Loading error={d.error} onRetry={d.reload} /> : (
          <>
            {v.buildings.length > 1 && (
              <Tabs value={v.building.id} onChange={(b) => { setFloor(null); navigate(`/b/${b}`); }}
                items={v.buildings.map((b: any) => ({ value: b.id, label: `${b.name} (${b.floors} эт.)` }))} />
            )}
            {!v.cycle ? <Empty>План корпуса ещё не начат или все этажи сданы. Старт и шаблон цикла — в «Администрировании».</Empty> : (
              <>
                <StatusLine v={v} />
                {ai && (
                  <div className="ai stack tight" role="region" aria-label="Сводка, подготовленная автоматически">
                    <span className="badge">Сформировано автоматически — проверьте перед решением</span>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>{ai.facts.map((f: string, i: number) => <li key={i}>{f}</li>)}</ul>
                    {ai.conclusions.map((c: string, i: number) => <p key={i}><b>{c}</b></p>)}
                  </div>
                )}
                <Kpis v={v} />
                <div className="prod-grid">
                  <div className="panel area-grips">
                    <div className="ph"><h2 className="grow">Захватки — {v.building.name} / {floorName(v.floor, false)}</h2><Button size="small" variant="ghost" onClick={() => navigate(`/b/${id}/grid`)}>Шахматка</Button></div>
                    <GripsTable rows={v.grips} />
                  </div>
                  <div className="panel area-pf">
                    <div className="ph"><h2 className="grow">План и факт бетона по этажам, м³</h2></div>
                    <div className="pb stack">
                      <PlanFactBars rows={v.plan_fact_by_floor} unit="м³" />
                      <div className="legend fills small"><span><i className="sw-plan" />план</span><span><i className="sw-fact" />факт</span></div>
                      <div className="mini-kpis">
                        <div><div className="k">Средний цикл этажа</div><div className="v">{v.cycle_days.avg_fact != null ? `${fmtNum(v.cycle_days.avg_fact, 1)} дн.` : "—"}</div>
                          {v.cycle_days.diff != null && <div className={`s ${v.cycle_days.diff > 0 ? "bad" : "good"}`}>{v.cycle_days.diff > 0 ? "+" : ""}{fmtNum(v.cycle_days.diff, 1)} к плану ({v.cycle_days.plan} дн.)</div>}</div>
                        <div><div className="k">Производительность</div><div className="v">{v.productivity.m3_per_person != null ? `${fmtNum(v.productivity.m3_per_person, 2)} м³` : "—"}</div>
                          <div className="s">на человеко-смену за {v.productivity.days} дн.{v.productivity.change_pct != null ? `; ${v.productivity.change_pct > 0 ? "+" : ""}${v.productivity.change_pct}% к прошлым ${v.productivity.days} дн.` : ""}</div></div>
                      </div>
                    </div>
                  </div>
                  <div className="area-side stack">
                    <div className="panel">
                      <div className="ph"><h2>Быстрые действия</h2></div>
                      <div className="pb quick">
                        <button className="qa red" onClick={() => navigate(`/b/${id}/issues?new=1`)}><Icon name="issue" />Создать проблему</button>
                        <button className="qa blue" onClick={() => navigate(`/b/${id}/report`)}><Icon name="report" />Отчёт за смену</button>
                        <button className="qa violet" onClick={() => navigate(`/b/${id}/schedule`)}><Icon name="schedule" />График цикла</button>
                        <button className="qa green" onClick={() => navigate(`/b/${id}/photos`)}><Icon name="photo" />Фотоархив</button>
                      </div>
                    </div>
                    <Events rows={v.events} />
                  </div>
                  <div className="panel area-gantt">
                    <div className="ph"><h2 className="grow">Производственный график — {v.building.name}</h2><Button size="small" variant="ghost" onClick={() => navigate(`/b/${id}/schedule?mode=range`)}>Подробно</Button></div>
                    <div className="pb">
                      <FloorsGantt rows={v.floors_gantt} today={v.as_of} />
                      <div className="legend fills small"><span><i className="sw-plan" />план</span><span><i className="sw-done" />выполнен</span><span><i className="sw-planned" />идёт</span><span><i className="sw-loading" />идёт позже плана</span><span><i className="sw-none" style={{ borderStyle: "dashed" }} />прогноз</span></div>
                    </div>
                  </div>
                  <div className="panel area-mat">
                    <div className="ph"><h2>Материалы — {v.building.name}</h2></div>
                    <Materials rows={v.materials} />
                  </div>
                </div>
                <Decisions rows={v.decisions} id={id} />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function StatusLine({ v }: { v: any }) {
  const late = v.cycle.deviation_days;
  const cls = v.status === "behind" ? "behind" : "ok";
  return (
    <div className={`status-line ${cls}`}>
      <b>{v.status === "behind" ? `Отставание ${days(late)}` : v.status === "ahead" ? `Опережение ${days(-late)}` : "По плану"}</b>
      <span>{floorName(v.cycle.floor, false)}: окончание {fmtDate(v.cycle.forecast_end)}{late ? ` вместо ${fmtDate(v.cycle.planned_end)}` : ""}</span>
    </div>
  );
}

function Kpis({ v }: { v: any }) {
  const c = v.volumes?.concrete_m3, r = v.volumes?.rebar_t;
  const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
  const people = v.people;
  return (
    <div className="kpi-row">
      <div className="kpi wide">
        <div className="k">Общая готовность ({v.building.name})</div>
        <div className="row" style={{ gap: 14, flexWrap: "nowrap" }}>
          <Ring value={v.progress.fact} label={`Готовность корпуса ${Math.round(v.progress.fact)}%`} />
          <div className="stack tight small">
            <div><b style={{ fontSize: 18 }}>{v.floors_done} / {v.floors_total}</b> этажей сдано</div>
            <div className="muted">по плану на сегодня {fmtNum(v.progress.plan, 0)}%</div>
            <div className="muted">цикл: план {v.cycle_days.plan} дн., факт {v.cycle_days.avg_fact != null ? fmtNum(v.cycle_days.avg_fact, 1) : "—"}</div>
          </div>
        </div>
      </div>
      <VolKpi title="Бетон, этаж" unit="м³" v={c} digits={0} />
      <VolKpi title="Арматура, этаж" unit="т" v={r} digits={1} />
      <div className="kpi">
        <div className="k">Опалубка, этаж</div>
        <div className="v">{v.formwork_pct != null ? `${v.formwork_pct}%` : "—"}</div>
        <Bar value={v.formwork_pct || 0} warn={(v.formwork_pct ?? 100) < (v.cycle.planned_progress_pct ?? 0)} />
      </div>
      <div className="kpi">
        <div className="k">Люди, факт / план</div>
        <div className="v">{people.fact ?? "—"} / {people.plan ?? "—"}</div>
        <Bar value={pct(people.fact || 0, people.plan || 1)} warn={people.fact != null && people.plan && people.fact < people.plan * (v.headcount_ratio ?? 0.8)} />
        <div className="s">{v.cycle.floor && `в смену, последний отчёт`}</div>
      </div>
    </div>
  );
}

function VolKpi({ title, unit, v, digits }: { title: string; unit: string; v: any; digits: number }) {
  if (!v) return null;
  const p = v.plan ? Math.round((v.fact / v.plan) * 100) : 0;
  return (
    <div className="kpi">
      <div className="k">{title}</div>
      <div className="v">{fmtNum(v.fact, digits)} <span className="muted" style={{ fontSize: 15 }}>/ {fmtNum(v.plan, digits)} {unit}</span></div>
      <Bar value={p} warn={v.fact < v.plan_to_date * 0.95} />
      <div className="s">{p}% · по плану на сегодня {fmtNum(v.plan_to_date, digits)}</div>
    </div>
  );
}

function GripsTable({ rows }: { rows: any[] }) {
  const cell = (x: number | null) => x == null ? <span className="muted">—</span> : (
    <span className="pctc"><i className={x >= 100 ? "d-done" : x > 0 ? "d-run" : "d-none"} />{fmtNum(x, 0)}%</span>
  );
  return (
    <div className="table-wrap">
      <table className="table grips">
        <thead><tr><th>Захватка</th><th>Арматура</th><th>Опалубка</th><th>Бетон</th><th>Статус</th><th className="num">Готово</th><th className="num">Отставание</th></tr></thead>
        <tbody>{rows.map((g) => (
          <tr key={g.grip}>
            <td title={g.grip.startsWith("С") ? "захватка стен" : g.grip.startsWith("У") ? "участок этапа" : "захватка перекрытия"}><b>{g.label}</b></td>
            <td>{cell(g.rebar)}</td><td>{cell(g.form)}</td><td>{cell(g.concrete)}</td>
            <td><span className={`tag ${GRIP_ST[g.status]?.cls}`}>{GRIP_ST[g.status]?.label || g.status}</span></td>
            <td className="num strong">{g.ready}%</td>
            <td className={`num${g.lag_days > 0 ? " late" : ""}`}>{g.lag_days > 0 ? `+${fmtNum(g.lag_days, 1)} дн.` : g.status === "done" ? "0 дн." : "—"}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Materials({ rows }: { rows: any[] }) {
  if (!rows.length) return <p className="muted pb">Объёмов пока нет.</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>Материал</th><th className="num" title="План к сегодняшнему дню">План</th><th className="num">Факт</th><th className="num">Откл.</th></tr></thead>
        <tbody>{rows.map((m) => (
          <tr key={m.key}>
            <td>{m.name}, {m.unit}</td><td className="num">{fmtNum(m.plan, 0)}</td><td className="num">{fmtNum(m.fact, 0)}</td>
            <td className={`num${m.deviation_pct != null && Math.abs(m.deviation_pct) >= 3 ? " late" : ""}`}>{m.deviation_pct == null ? "—" : `${m.deviation_pct > 0 ? "+" : ""}${fmtNum(m.deviation_pct, 1)}%`}</td>
          </tr>
        ))}</tbody>
      </table>
      <p className="small muted" style={{ padding: "6px 16px 10px", margin: 0 }}>Все начатые этажи. Опалубка — по проценту выполнения работ, остальное — по объёмам из отчётов.</p>
    </div>
  );
}

function Events({ rows }: { rows: any[] }) {
  return (
    <div className="panel">
      <div className="ph"><h2>Ключевые события</h2></div>
      <div className="pb events">
        {rows.length === 0 && <p className="muted" style={{ margin: 0 }}>За двое суток событий нет.</p>}
        {rows.map((e, i) => (
          <div key={i} className={`ev ${e.level}`}>
            <i />
            <div className="grow"><div>{e.text}</div>{e.where && <div className="small muted">{e.where}</div>}</div>
            <span className="small muted num">{fmtTime(e.at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Decisions({ rows, id }: { rows: any[]; id?: string }) {
  if (!rows.length) return null;
  return (
    <div className="decisions">
      <div className="t"><Icon name="issue" />Требует решения · {rows.length}</div>
      <div className="list">
        {rows.slice(0, 4).map((r, i) => (
          <button key={r.id} className="item" onClick={() => navigate(`/b/${r.building_id || id}/issues${r.kind === "plan_request" ? "?tab=requests" : ""}`)}>
            <span className="n">{i + 1}.</span>
            <span className="grow"><b>{r.title}</b><span className="small muted">{[r.building, r.where, r.detail].filter(Boolean).join(" · ")}</span></span>
          </button>
        ))}
      </div>
      {rows.length > 4 && <span className="small muted">и ещё {rows.length - 4} — в «Проблемах»</span>}
    </div>
  );
}
